/**
 * AdminMotion — excellence primitives (press + page enter).
 * Press/hover use CSS transform (GPU) when possible; Motion only for route opacity.
 */

import { motion, useReducedMotion } from "framer-motion";
import type { HTMLAttributes, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { ADMIN_PAGE_ENTER } from "@/lib/adminMotion";
import { cn } from "@/lib/utils";

type PressableProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  /** Stronger press (default buttons). */
  strength?: "soft" | "default";
  className?: string;
  disabled?: boolean;
};

/**
 * Pointer-down scale feedback via CSS (transform only — GPU, interruptible).
 * Prefer wrapping interactive surfaces; native buttons also get CSS :active in admin-app-shell.
 */
export function AdminPressable({
  children,
  strength = "default",
  className,
  disabled,
  ...props
}: PressableProps) {
  const reduce = useReducedMotion();
  const pressScale = strength === "soft" ? "active:scale-[0.985]" : "active:scale-[0.97]";

  if (disabled || reduce) {
    return (
      <div className={cn(className, disabled && "pointer-events-none opacity-60")}>{children}</div>
    );
  }

  return (
    <div
      className={cn(
        "origin-center transition-transform duration-150 ease-out motion-reduce:transition-none",
        pressScale,
        "motion-reduce:active:scale-100",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

type CardMotionProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  interactive?: boolean;
  className?: string;
};

/** KPI / clickable cards — soft hover + press via CSS (hover only when supported via Tailwind future flag). */
export function AdminCardMotion({
  children,
  interactive = true,
  className,
  ...props
}: CardMotionProps) {
  const reduce = useReducedMotion();
  if (!interactive || reduce) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      className={cn(
        "origin-center transition-transform duration-150 ease-out",
        "hover:scale-[1.01] active:scale-[0.985]",
        "motion-reduce:transition-none motion-reduce:hover:scale-100 motion-reduce:active:scale-100",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Route-keyed content enter (opacity only). Nested under AdminLayout Outlet.
 * CSS `.admin-page-content` also fades; this is the interruptible Motion layer.
 */
export function AdminPageTransition({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const reduce = useReducedMotion();

  return (
    <motion.div
      key={pathname}
      initial={reduce ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={reduce ? { duration: 0.12 } : ADMIN_PAGE_ENTER}
      className="min-w-0"
    >
      {children}
    </motion.div>
  );
}
